import React, { useEffect, useState } from 'react'
import withTranslation from 'Common/withTranslation';
import { WithTranslationProps } from 'Common/Components/Translation/WithTranslationProps';
import { AccountInformationEditorProps } from './AccountInformationEditorProps';
import Subtitle from 'Common/Components/Titles/Subtitle';
import { X } from 'lucide-react';
import { AccountEditorViewModel } from 'Common/ViewModels/UserProfile/AccountEditor/AccountEditorViewModel';
import { AccountEditorErrorsViewModel } from 'Common/ViewModels/Errors/UserProfile/AccountEditor/AccountEditorErrorsViewModel';
import LoadingButton from 'Common/Components/LoadingOverlay/LoadingButton/LoadingButton';
import withMessages, { WithMessagesProps } from 'Common/withMessages';
import withNotifications, { WithNotificationsProps } from 'Common/withNotifications';
import { MessageType } from 'Common/Components/Messages/messageTypes';
import { NotificationType } from 'Common/Components/Notifications/notificationTypes';
import { axios_helper_async } from 'helpers/Axios';

const AccountInformationEditor: React.FC<WithTranslationProps & AccountInformationEditorProps & WithMessagesProps & WithNotificationsProps> = ({
    customer,
    isOpen,
    onClose,
    close,
    t,
    messages,
    notifications
}) => {
    const modelFields: Record<keyof AccountEditorViewModel, string> = {
        id: 'id',
        email: 'email',
        firstName: 'firstName',
        lastName: 'lastName',
        phone: 'phone'
    }
    const initialModel: AccountEditorViewModel = {
        id: customer.id,
        email: customer.email,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone
    }
    const [model, setModel] = useState<AccountEditorViewModel>(initialModel);
    const [errors, setErrors] = useState<AccountEditorErrorsViewModel>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleSave = async () => {
        try {
            setIsLoading(true);
            if (validModel()) {
                const response = await axios_helper_async<boolean>("/Account/Update-Customer-Information", 'PATCH', model);

                messages.setMessages(response.messages);
                notifications.setNotifications(response.notifications);

                if (response.isSuccess) {
                    if (response.data) {
                        notifications.showNotification("UserProfile.AccountEditor.Successes.Save", NotificationType.Success);
                        close();
                    }
                    else {
                        messages.showMessage("", MessageType.Error, "UserProfile.AccountEditor.Errors.Save");
                    }
                }
                else {
                    messages.showMessage("UserProfile.Errors.GetCustomerInformation", MessageType.Error, "Error");
                }
            }
        }
        catch (err: any) {
            notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
        }
        finally {
            setIsLoading(false);
        }
    }

    const validModel = () => {
        let newErrors: AccountEditorErrorsViewModel = {};

        if (!model.firstName) newErrors.firstName = "UserProfile.AccountEditor.Errors.FirstName";
        if (!model.lastName) newErrors.lastName = "UserProfile.AccountEditor.Errors.LastName";
        if (!model.email) newErrors.email = "UserProfile.AccountEditor.Errors.Email";

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setModel(prevModel => ({
            ...prevModel,
            [name]: value
        }));
    }

    useEffect(() => {
        if (customer) {
            setModel(customer);
        }
    }, [customer])

    return (
        <React.Fragment>
            {
                isOpen ?
                    <div className="flex justify-center items-center overflow-hidden fixed inset-0 z-[1005] p-2 min-h-screen">
                        <div className="my-8 mx-auto max-w-sm sm:max-w-md md:max-w-lg z-[47] bg-white rounded-lg shadow-lg overflow-hidden w-full">
                            <div className="flex flex-col items-center p-6 text-center w-full gap-2">
                                <div className="flex flex-row justify-center items-center w-full">
                                    <div className="flex justify-center items-center w-full">
                                        {/* <h3 className="text-md font-bold text-black w-full">{t("Product.ProductRequest.Title")}</h3> */}
                                        <Subtitle text={t("UserProfile.AccountEditor.Title")}></Subtitle>
                                    </div>
                                    {!isLoading ?
                                        <div className="flex justify-center items-center">
                                            <button onClick={close}><X></X></button>
                                        </div>
                                        :
                                        null
                                    }
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.AccountEditor.FirstName")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.firstName ? "border-red-500" : null}`}
                                        id={modelFields.firstName}
                                        name={modelFields.firstName}
                                        value={model.firstName}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.AccountEditor.FirstName")}
                                    />
                                    {errors && errors.firstName && <div className="mt-1 text-sm text-red-500">{t(errors.firstName)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.AccountEditor.LastName")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.lastName ? "border-red-500" : null}`}
                                        id={modelFields.lastName}
                                        name={modelFields.lastName}
                                        value={model.lastName}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.AccountEditor.LastName")}
                                    />
                                    {errors && errors.lastName && <div className="mt-1 text-sm text-red-500">{t(errors.lastName)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.AccountEditor.Email")}<span className="text-red-500">*</span></label>
                                    <input
                                        className={`form-input ${errors?.email ? "border-red-500" : null}`}
                                        id={modelFields.email}
                                        name={modelFields.email}
                                        value={model.email}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.AccountEditor.Email")}
                                        readOnly={true}
                                    />
                                    {errors && errors.email && <div className="mt-1 text-sm text-red-500">{t(errors.email)}</div>}
                                </div>
                                <div className="flex flex-col justify-center items-center w-full gap-2">
                                    <label className="form-label">{t("UserProfile.AccountEditor.Phone")}</label>
                                    <input
                                        className="form-input"
                                        id={modelFields.phone}
                                        name={modelFields.phone}
                                        value={model.phone}
                                        onChange={handleChange}
                                        placeholder={t("UserProfile.AccountEditor.Phone")}
                                    />
                                </div>
                                {
                                    isLoading ?
                                        <LoadingButton />
                                        :
                                        <button className="btn btn-custom flex flex-row justify-center items-center gap-2 w-full" onClick={handleSave}>{t("UserProfile.AccountEditor.Save")}</button>
                                }
                            </div>
                        </div>
                        <div className="opacity-50 fixed inset-0 z-[46] bg-black"></div>
                    </div>
                    :
                    null
            }
        </React.Fragment>
    );
}

export default withTranslation(withMessages(withNotifications(AccountInformationEditor)));